import { pluralize } from "@prismicio/editor-support/String"
import { isDefined } from "@prismicio/editor-support/Type"
import clsx from "clsx"
import { type ReactNode, useState } from "react"

import { BlankSlate, BlankSlateIcon, BlankSlateTitle } from "../BlankSlate"
import { Box } from "../Box"
import styles from "./FileDropZone.module.css"
import { isValidFileExtension, type MediaAssetType } from "./FileUpload.utils"

export interface FileDropZoneProps {
  onFilesSelected: (files: File[]) => void
  overlay: ReactNode
  assetType?: MediaAssetType
  children?: ReactNode
  maxFiles?: number
}

export function FileDropZone(props: FileDropZoneProps) {
  const {
    onFilesSelected,
    overlay,
    children,
    assetType = "all",
    maxFiles = Infinity,
  } = props

  const [isDropTarget, setIsDropTarget] = useState(false)
  const [isTooManyFiles, setIsTooManyFiles] = useState(false)

  function validDropOperation(event: React.DragEvent<HTMLDivElement>) {
    if (!event.dataTransfer.items) return false

    // Check number of files
    if (event.dataTransfer.items.length > maxFiles) return false

    // Check if file type matches a valid asset type
    if (assetType !== "all") {
      const hasMatch = [...event.dataTransfer.items].every((item) =>
        isValidFileExtension(item.type, assetType),
      )
      if (!hasMatch) return false
    }

    return true
  }

  function validateFiles(event: React.DragEvent<HTMLDivElement>) {
    if (event.dataTransfer.items.length > maxFiles) {
      setIsTooManyFiles(true)
    }
  }

  function onDragEnter(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault()
    const isValidDropOperation = validDropOperation(event)
    setIsDropTarget(isValidDropOperation)
    validateFiles(event)
  }

  function onDragOver(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault()
    const isValidDropOperation = validDropOperation(event)
    setIsDropTarget(isValidDropOperation)
    validateFiles(event)
  }

  function onDragLeave(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault()
    setIsDropTarget(false)
    setIsTooManyFiles(false)
  }

  async function onDrop(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault()
    setIsTooManyFiles(false)

    if (!validDropOperation(event)) return

    const fileDropItems = [...event.dataTransfer.items].filter(
      (item) =>
        item.kind === "file" &&
        (assetType === "all"
          ? true
          : isValidFileExtension(item.type, assetType)),
    )
    const newFiles = (
      await Promise.all(fileDropItems.map((item) => item.getAsFile()))
    ).filter(isDefined)

    setIsDropTarget(false)
    onFilesSelected(newFiles)
  }

  return (
    <div
      className={clsx(
        styles.root,
        isDropTarget && styles.dropTarget,
        isTooManyFiles && styles.isTooManyFiles,
      )}
    >
      <div
        aria-label="dropzone"
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDragOver={onDragOver}
        onDrop={onDrop}
        className={styles.dropzone}
      >
        {children}
      </div>
      {isDropTarget && (
        <div className={styles.overlayContainer}>
          <div className={styles.overlay}>{overlay}</div>
        </div>
      )}
      {isTooManyFiles && (
        <div className={styles.overlayContainer}>
          <div className={styles.overlay}>
            <Box
              justifyContent="center"
              flexDirection="column"
              height="100%"
              padding={16}
            >
              <BlankSlate variant="horizontal">
                <BlankSlateIcon
                  name="block"
                  lineColor="tomato11"
                  backgroundColor="tomato2"
                  borderColor="tomato6"
                  borderStyle="dashed"
                  size="small"
                />
                <BlankSlateTitle size="regular">
                  You can only upload {maxFiles} {pluralize(maxFiles, "file")}
                </BlankSlateTitle>
              </BlankSlate>
            </Box>
          </div>
        </div>
      )}
    </div>
  )
}
